import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Per-column pixel widths with drag handles on table headers.
 * @param {Array<{ key: string, defaultWidth: number, minWidth?: number }>} columnDefs
 * @param {string} [storageKey] localStorage key for persisted widths
 * @param {string} [legacyStorageKey] optional previous key to migrate from
 */
export default function useResizableTableColumns(columnDefs, storageKey, legacyStorageKey) {
  const defsRef = useRef(columnDefs);
  defsRef.current = columnDefs;

  const [widths, setWidths] = useState(() => {
    const defaults = Object.fromEntries(
      columnDefs.map((c) => [c.key, c.defaultWidth])
    );
    if (!storageKey) return defaults;
    try {
      let raw = localStorage.getItem(storageKey);
      if (!raw && legacyStorageKey) {
        raw = localStorage.getItem(legacyStorageKey);
      }
      if (!raw) return defaults;
      const parsed = JSON.parse(raw);
      const merged = { ...defaults };
      columnDefs.forEach((c) => {
        const w = Number(parsed[c.key]);
        if (Number.isFinite(w) && w >= (c.minWidth ?? 48)) {
          merged[c.key] = w;
        }
      });
      return merged;
    } catch {
      return defaults;
    }
  });

  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify(widths));
    } catch {
      /* ignore quota / private mode */
    }
  }, [widths, storageKey]);

  const widthsRef = useRef(widths);
  widthsRef.current = widths;

  const resizingRef = useRef(null);
  const [resizingKey, setResizingKey] = useState(null);

  const endResize = useCallback(() => {
    resizingRef.current = null;
    setResizingKey(null);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  const onMove = useCallback((e) => {
    const r = resizingRef.current;
    if (!r) return;
    e.preventDefault();
    const def = defsRef.current.find((c) => c.key === r.key);
    const minW = def?.minWidth ?? 48;
    const next = Math.max(minW, r.startWidth + (e.clientX - r.startX));
    setWidths((prev) => (prev[r.key] === next ? prev : { ...prev, [r.key]: next }));
  }, []);

  useEffect(() => {
    const onUp = () => {
      if (!resizingRef.current) return;
      endResize();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [onMove, endResize]);

  const startResize = useCallback((key, event) => {
    event.preventDefault();
    event.stopPropagation();
    const w = widthsRef.current[key];
    if (!Number.isFinite(w)) return;
    resizingRef.current = { key, startX: event.clientX, startWidth: w };
    setResizingKey(key);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const getColWidth = useCallback((key) => widths[key], [widths]);

  return { widths, getColWidth, startResize, resizingKey };
}
